/**
 * Koji CZ — přidání balíčku do košíku (multi-line) + otevření cart draweru.
 *
 * Deal-bary (koji-packages.liquid) drží výběr balíčku + barev v DOM.
 * Tento modul na kliknutí tlačítka posbírá vybrané varianty, přidá je jako
 * samostatné položky s vlastností `_bundle` (kterou čte discount function),
 * a přes Horizon event `CartLinesUpdateEvent` nechá cart drawer refreshnout
 * a otevřít se — stejně jako nativní add-to-cart.
 *
 * Fallback: při jakékoli chybě přesměruje na /cart (staré chování).
 */

import { CartLinesUpdateEvent } from '@shopify/events';

function routeBase() {
  const r =
    window.Shopify && Shopify.routes && Shopify.routes.root
      ? Shopify.routes.root
      : '/';
  return r.replace(/\/$/, '');
}

function collectItems(root) {
  const deals = root.querySelector('[data-koji-deals]');
  if (!deals) return null;
  const active = deals.querySelector('.deal.is-active');
  if (!active) return null;

  let variants = {};
  const vEl = root.querySelector('[data-koji-variants]');
  if (vEl) {
    try {
      variants = JSON.parse(vEl.textContent);
    } catch (e) {
      /* noop */
    }
  }

  const bundleKey = active.getAttribute('data-bundle') || '';
  const bundleName = active.getAttribute('data-bundle-name') || '';

  // agregace stejných barev do quantity
  const rows = active.querySelectorAll('.color-row');
  const counts = {};
  rows.forEach((rowEl) => {
    const sel =
      rowEl.querySelector('.sw.is-active') || rowEl.querySelector('.sw');
    if (!sel) return;
    const id = variants[sel.getAttribute('data-color')];
    if (!id) return;
    counts[id] = (counts[id] || 0) + 1;
  });

  const items = Object.keys(counts).map((id) => {
    const properties = {};
    if (bundleKey) {
      properties._bundle = bundleKey;
      // Název balíčku a SLÍBENÝ počet kusů jdou jako SKRYTÉ vlastnosti
      // (podtržítko). Viditelnou vlastnost Shopify vypisuje v košíku,
      // v potvrzení objednávky i v e-mailu — a popisek „Sada 2+1 zdarma"
      // přežije snížení množství v košíku: sleva (Shopify Function na
      // množstevní tier) přestane platit, cena se vrátí na 499 Kč za kus,
      // ale popisek zůstane viset nad jedním ručníkem. Zákaznice pak čte
      // popisek, ne cenu — reklamace #1842, 14 objednávek celkem.
      // Popisek renderuje snippets/cart-products.liquid, a jen tehdy,
      // když je sada kompletní.
      if (bundleName) properties._bundle_name = bundleName;
      properties._bundle_units = String(rows.length);
    }
    return { id: Number(id), quantity: counts[id], properties };
  });

  return items.length ? items : null;
}

async function addBundle(root, btn) {
  const items = collectItems(root);
  if (!items) return;

  const base = routeBase();

  btn.setAttribute('disabled', 'disabled');
  btn.classList.add('is-loading');

  // sekce k re-renderu (řádky košíku / drawer)
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el.dataset && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  const sections = Array.from(sectionIds).join(',');

  const totalQty = items.reduce((sum, it) => sum + it.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  // oznam téma, že se přidává (drawer s auto-open se otevře na 'add')
  root.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({
        merchandiseId: 'gid://shopify/ProductVariant/' + it.id,
        quantity: it.quantity,
      })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(base + '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) throw new Error('add failed');
    const data = await res.json();

    let cart = null;
    try {
      const cres = await fetch(base + '/cart.js', {
        headers: { Accept: 'application/json' },
      });
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(await cres.json());
    } catch (e) {
      /* count fallback */
    }

    deferred.resolve({
      cart,
      detail: {
        sections: data.sections,
        items: data.items,
        source: 'koji-bundle',
        itemCount: totalQty,
      },
    });
  } catch (e) {
    if (typeof deferred.reject === 'function') deferred.reject(e);
    window.location.href = base + '/cart';
    return;
  } finally {
    btn.removeAttribute('disabled');
    btn.classList.remove('is-loading');
  }
}

function init() {
  document.querySelectorAll('.koji-packages').forEach((root) => {
    if (root.dataset.kojiAddInit) return;
    root.dataset.kojiAddInit = '1';
    const btn = root.querySelector('[data-koji-add]');
    if (!btn) return;
    btn.addEventListener('click', () => {
      addBundle(root, btn);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
